import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAcademyStore } from '../../store/academyStore';
import { COURSES } from '../../data/curriculum';

export const GorillaVoiceManager = () => {
    const { settings } = useAcademyStore();
    const location = useLocation();
    const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const getGorillaVoice = () => {
        const synth = window.speechSynthesis;
        const voices = synth.getVoices();
        // Heuristic to find a deeper/authoritative voice
        // 'Google US English' can be pitch-shifted well.
        // 'Microsoft David' is good.
        // 'Daniel' (UK) is clear.
        return voices.find(v => v.name.includes('David')) ||
            voices.find(v => v.name.includes('Google US English')) ||
            voices[0];
    };

    const speak = (text: string) => {
        const synth = window.speechSynthesis;
        if (!text || text.trim().length === 0) return;

        synth.cancel(); // Abort previous standard reading

        const utterance = new SpeechSynthesisUtterance(text);
        utterance.pitch = 0.9; // More natural, slightly deep
        utterance.rate = 1.0;
        utterance.volume = 1.0;

        const voice = getGorillaVoice();
        if (voice) utterance.voice = voice;

        synth.speak(utterance);
    };

    // Announce Page Changes
    useEffect(() => {
        if (!settings.voiceEnabled) return;

        const path = location.pathname.substring(1); // remove leading slash

        if (path === '' || path === '/') {
            speak("Welcome to Grounded Growth Academy Dashboard.");
            return;
        }

        // Check if it's a module
        let title = '';

        // Find in courses
        for (const course of COURSES) {
            const module = course.modules.find(m => m.id === path || m.path === path);
            if (module) {
                title = `Opening Module: ${module.title}`;
                break;
            }
        }

        // Check known routes
        if (!title) {
            if (path.startsWith('games')) title = "Entering Arcade";
            else if (path === 'profile') title = "User Profile";
            else if (path === 'manual') title = "Student Manual";
            else if (path === 'diploma') title = "Grand Diploma";
        }

        if (title) {
            // Small delay to allow page load
            setTimeout(() => speak(title), 500);
        }

    }, [location.pathname, settings.voiceEnabled]);

    useEffect(() => {
        if (!settings.voiceEnabled) {
            window.speechSynthesis.cancel();
            return;
        }

        const handleMouseOver = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (!target) return;

            // Relevant Tags
            const validTags = ['P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'LI', 'BUTTON', 'A', 'BLOCKQUOTE', 'SPAN', 'TD', 'TH', 'LABEL', 'INPUT'];
            const tag = target.tagName;

            if (validTags.includes(tag)) {

                let text = target.innerText || target.getAttribute('aria-label') || target.title;

                // Form elements specific
                if (tag === 'INPUT') {
                    const input = target as HTMLInputElement;
                    text = input.placeholder || input.name;
                }

                if (!text) return;

                // Cleanup text
                text = text.split('\n')[0]; // Read first line only for complex elements
                if (text.length > 100) text = text.substring(0, 100) + "..."; // Truncate long text

                // Debounce to allow moving mouse across screen without chatter
                if (hoverTimer.current) clearTimeout(hoverTimer.current);

                hoverTimer.current = setTimeout(() => {
                    speak(text);
                }, 400); // 400ms pause before speaking
            }
        };

        const handleMouseOut = () => {
            // If mouse leaves quickly, cancel the pending timer
            if (hoverTimer.current) clearTimeout(hoverTimer.current);
            window.speechSynthesis.cancel();
        };

        document.addEventListener('mouseover', handleMouseOver);
        document.addEventListener('mouseout', handleMouseOut);

        speak("Narrator On");

        return () => {
            document.removeEventListener('mouseover', handleMouseOver);
            document.removeEventListener('mouseout', handleMouseOut);
            if (hoverTimer.current) clearTimeout(hoverTimer.current);
            window.speechSynthesis.cancel();
        };
    }, [settings.voiceEnabled]);

    return null;
};
